import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAdAccount } from "../lib/adAccount";
import {
  Trophy, Warning, Skull, Pulse, Sparkle, X, ChatTeardropDots,
  CurrencyCircleDollar, UserPlus, CursorClick, Calendar
} from "@phosphor-icons/react";
import DateRangePicker from "../components/DateRangePicker";
import RefreshButton from "../components/RefreshButton";
import LiveSyncButton from "../components/LiveSyncButton";

const fmtMoney = (n) => "RM" + (n || 0).toLocaleString("en-MY", { maximumFractionDigits: 2 });
const fmtNum = (n) => (n || 0).toLocaleString("en-MY");

const VERDICTS = {
  winner: { icon: Trophy, label: "WINNER", color: "text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/5", desc: "Low CPS + strong subscribe volume — scale this." },
  fatigued: { icon: Warning, label: "FATIGUED", color: "text-amber-400", border: "border-amber-500/40", bg: "bg-amber-500/5", desc: "CTR dropping vs prior period — refresh creative." },
  loser: { icon: Skull, label: "LOSER", color: "text-red-400", border: "border-red-500/40", bg: "bg-red-500/5", desc: "Wasted spend with poor subscribe rate — pause." },
  normal: { icon: Pulse, label: "AVERAGE", color: "text-zinc-400", border: "border-zinc-800", bg: "bg-zinc-900/30", desc: "Performing within normal range — no immediate action needed." },
};

const FILTERS = [
  { key: "all", label: "All", icon: Pulse },
  { key: "winner", label: "Winners", icon: Trophy },
  { key: "normal", label: "Average", icon: Pulse },
  { key: "fatigued", label: "Fatigued", icon: Warning },
  { key: "loser", label: "Losers", icon: Skull },
];

function CreativeCard({ ad, onOpen, onAskAI }) {
  const v = VERDICTS[ad.verdict];
  const Icon = v.icon;
  const m = ad.metrics;
  return (
    <div
      data-testid={`creative-card-${ad.id}`}
      onClick={() => onOpen(ad)}
      className={`bg-[#0a0a0a] border ${v.border} ${v.bg} hover:border-orange-500/60 transition-colors cursor-pointer flex flex-col`}
    >
      {/* Image */}
      <div className="relative aspect-[1.91/1] bg-zinc-900 overflow-hidden">
        {ad.creative?.image_url && (
          <img src={ad.creative.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        )}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-black/70 backdrop-blur-md">
          <Icon size={11} weight="fill" className={v.color} />
          <span className={`text-[10px] font-mono uppercase tracking-wider ${v.color}`}>{v.label}</span>
        </div>
        <div className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 bg-black/70 backdrop-blur-md text-[10px] font-mono uppercase tracking-wider ${ad.status === "ACTIVE" ? "text-emerald-400" : "text-zinc-500"}`}>
          <span className={`w-1 h-1 ${ad.status === "ACTIVE" ? "bg-emerald-400" : "bg-zinc-500"}`} />
          {ad.status}
        </div>
      </div>

      {/* Creative text */}
      <div className="p-3 border-b border-zinc-800/40">
        <div className="text-sm font-medium text-zinc-100 leading-tight line-clamp-2 mb-1">
          {ad.creative?.headline || ad.name}
        </div>
        <div className="text-[11px] text-zinc-500 line-clamp-2 leading-relaxed">
          {ad.creative?.body}
        </div>
        {ad.creative?.cta_type && (
          <div className="inline-block mt-2 text-[9px] font-mono uppercase tracking-wider text-orange-400 border border-orange-500/30 px-1.5 py-0.5">
            {ad.creative.cta_type.replace("_", " ")}
          </div>
        )}
      </div>

      {/* Metrics — 3 main: Spend, CTR, Subscribes */}
      <div className="p-3 grid grid-cols-3 gap-2 border-b border-zinc-800/40">
        <div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">Spend</div>
          <div className="text-[13px] font-display font-bold text-zinc-100 mt-0.5">{fmtMoney(m.spend)}</div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">CTR</div>
          <div className={`text-[13px] font-display font-bold mt-0.5 ${ad.verdict === "fatigued" ? "text-amber-400" : "text-zinc-100"}`}>
            {m.ctr}%
            {ad.verdict === "fatigued" && ad.prev_ctr > 0 && (
              <span className="text-[9px] font-mono text-amber-400 ml-1">↓{Math.round((1 - m.ctr / ad.prev_ctr) * 100)}%</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">Subs</div>
          <div className="text-[13px] font-display font-bold text-emerald-400 mt-0.5">{fmtNum(m.subscribes)}</div>
        </div>
      </div>

      {/* Footer: CPS + Ask AI */}
      <div className="p-2.5 flex items-center justify-between gap-2">
        <div className="text-[10px] font-mono">
          <span className="text-zinc-500">Cost/Sub </span>
          <span className={`font-semibold ${m.cost_per_subscribe > 0 && m.cost_per_subscribe <= 12 ? "text-emerald-400" : m.cost_per_subscribe <= 20 ? "text-zinc-200" : "text-red-400"}`}>
            {m.cost_per_subscribe ? fmtMoney(m.cost_per_subscribe) : "—"}
          </span>
        </div>
        <button
          data-testid={`ask-ai-${ad.id}`}
          onClick={(e) => { e.stopPropagation(); onAskAI(ad); }}
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-orange-400 hover:text-orange-300 border border-orange-500/30 hover:border-orange-500 px-2 py-1 transition-colors"
        >
          <Sparkle size={10} weight="fill" /> Ask AI
        </button>
      </div>
    </div>
  );
}

function DetailPanel({ ad, onClose, onAskAI }) {
  if (!ad) return null;
  const v = VERDICTS[ad.verdict];
  const Icon = v.icon;
  const m = ad.metrics;
  return (
    <div className="fixed inset-0 z-50" data-testid="creative-detail-panel">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0a0a0a] border-l border-zinc-800 overflow-y-auto">
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-zinc-800 px-4 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Icon size={14} weight="fill" className={v.color} />
            <span className={`text-[10px] font-mono uppercase tracking-wider ${v.color}`}>{v.label}</span>
          </div>
          <button data-testid="close-panel" onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-4">
          {ad.creative?.image_url && (
            <img src={ad.creative.image_url} alt="" className="w-full aspect-[1.91/1] object-cover" />
          )}

          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Creative</div>
            <h3 className="font-display text-lg font-semibold leading-tight">{ad.creative?.headline}</h3>
            <p className="text-sm text-zinc-400 mt-1.5">{ad.creative?.body}</p>
            {ad.creative?.cta_type && (
              <div className="inline-block mt-2 text-[10px] font-mono uppercase tracking-wider text-orange-400 border border-orange-500/30 px-2 py-0.5">
                {ad.creative.cta_type.replace("_", " ")}
              </div>
            )}
          </div>

          <div className={`p-3 ${v.bg} border ${v.border}`}>
            <div className={`text-[10px] font-mono uppercase tracking-wider ${v.color} mb-1`}>{v.label} · AI Verdict</div>
            <div className="text-xs text-zinc-300 leading-relaxed">{v.desc}</div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">Performance</div>
            <div className="grid grid-cols-2 gap-2">
              <Metric icon={CurrencyCircleDollar} label="Spend" value={fmtMoney(m.spend)} />
              <Metric icon={UserPlus} label="Subscribes" value={fmtNum(m.subscribes)} accent="text-emerald-400" />
              <Metric icon={CursorClick} label="CTR" value={m.ctr + "%"} sub={ad.prev_ctr ? `prev ${ad.prev_ctr}%` : null} />
              <Metric icon={CurrencyCircleDollar} label="Cost / Subscribe" value={m.cost_per_subscribe ? fmtMoney(m.cost_per_subscribe) : "—"} accent={m.cost_per_subscribe > 0 && m.cost_per_subscribe <= 12 ? "text-emerald-400" : m.cost_per_subscribe <= 20 ? "" : "text-red-400"} />
              <Metric label="Impressions" value={fmtNum(m.impressions)} />
              <Metric label="Clicks" value={fmtNum(m.clicks)} />
              <Metric label="LP Views" value={fmtNum(m.landing_page_views)} />
              <Metric icon={Calendar} label="Days Live" value={ad.days_live ? `${ad.days_live}d` : "—"} />
            </div>
          </div>

          <button
            data-testid="detail-ask-ai"
            onClick={() => onAskAI(ad)}
            className="w-full bg-orange-500 hover:bg-orange-600 text-zinc-950 font-semibold text-sm py-2.5 flex items-center justify-center gap-2"
          >
            <ChatTeardropDots size={14} weight="fill" /> Ask AI about this creative
          </button>
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 p-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} className="text-zinc-500" />}
        <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</span>
      </div>
      <div className={`text-sm font-display font-bold ${accent || "text-zinc-100"}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Creatives() {
  const navigate = useNavigate();
  const { selected } = useAdAccount();
  const [ads, setAds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [range, setRange] = useState({ mode: "preset", days: 7 });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [openAd, setOpenAd] = useState(null);

  const load = async () => {
    if (!selected) return;
    setLoading(true);
    const params = new URLSearchParams({ ad_account_id: selected });
    if (range.mode === "preset") params.set("days", range.days);
    else { params.set("date_start", range.date_start); params.set("date_stop", range.date_stop); }
    try {
      const r = await api.get(`/ads?${params}`);
      setAds(r.data || []);
      setLastUpdated(Date.now());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selected, range]); // eslint-disable-line

  const counts = ads.reduce((acc, a) => ({ ...acc, [a.verdict]: (acc[a.verdict] || 0) + 1, all: (acc.all || 0) + 1 }), {});

  const filtered = filter === "all" ? ads : ads.filter(a => a.verdict === filter);

  const askAI = (ad) => {
    const headline = ad.creative?.headline || ad.name;
    const prompt = `Analyze creative "${headline}" (verdict: ${ad.verdict}, spend RM${ad.metrics.spend}, subscribes ${ad.metrics.subscribes}, CTR ${ad.metrics.ctr}%, cost/subscribe RM${ad.metrics.cost_per_subscribe}). What should I do with this ad?`;
    sessionStorage.setItem("prefilledPrompt", prompt);
    navigate("/");
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-10">
        <div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Creatives</div>
          <div className="text-sm font-medium">Ad-level performance · Subscribe-focused</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <LiveSyncButton onSynced={load} />
          <RefreshButton lastUpdated={lastUpdated} loading={loading} onRefresh={load} />
          <DateRangePicker value={range} onChange={setRange} />
        </div>
      </div>

      <div className="p-6">
        {/* Filter chips */}
        <div className="flex items-center gap-1.5 mb-5 flex-wrap">
          {FILTERS.map(f => {
            const FIcon = f.icon;
            const active = filter === f.key;
            const count = counts[f.key] || 0;
            return (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors border ${
                  active ? "bg-orange-500 text-zinc-950 border-orange-500 font-semibold" : "border-zinc-800 hover:border-zinc-700 text-zinc-300"
                }`}
              >
                <FIcon size={12} weight={active ? "fill" : "regular"} />
                {f.label}
                <span className={`text-[10px] font-mono ${active ? "text-zinc-900" : "text-zinc-500"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {loading && <div className="text-zinc-500 text-sm">Loading creatives...</div>}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-500 text-sm">No creatives in this category</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(ad => (
            <CreativeCard key={ad.id} ad={ad} onOpen={setOpenAd} onAskAI={askAI} />
          ))}
        </div>
      </div>

      <DetailPanel ad={openAd} onClose={() => setOpenAd(null)} onAskAI={askAI} />
    </div>
  );
}
